import './App.css';

import {CssBaseline, ThemeProvider} from "@mui/material";
import {styled} from "@mui/material/styles";
import {SnackbarProvider, MaterialDesignContent} from "notistack";
import { QueryClientProvider } from '@tanstack/react-query';
import React from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";

import { baseTheme } from "./js/schema/theme/BaseTheme";
import { queryClient } from './js/api/queryClient';
import {useAuthStore} from "./js/stores";

import '../src/css/custom.css';
import '../src/css/button.css';
import '../src/css/code.css';
import '../src/css/content.css';
import '../src/css/team.css';

import Login from "./js/views/authentication/Login";
import Preloader from "./js/Preloader";
import ModalConfirmation from "./js/modal/ModalConfirmation";
import AppLayout from "./js/layouts/app/AppLayout";
import CampCreatePage from "./js/views/public/CampCreatePage";
import OrgInvitePage from "./js/views/public/OrgInvitePage";

const PUBLIC_ROUTES = ['/camp/create', '/invite'];

// Нотификации (notistack) под наш дизайн: крупнее текст из типографии, цвета палитры, скругление.
const AppSnackContent = styled(MaterialDesignContent)(() => ({
  '&.notistack-MuiContent': {
    fontFamily: "var(--sans)",
    fontSize: '17px',
    fontWeight: 600,
    letterSpacing: '-.005em',
    borderRadius: '12px',
    padding: '13px 18px',
    boxShadow: '0 1px 2px rgba(18,20,28,.05), 0 10px 26px -8px rgba(18,20,28,.28)',
  },
  '&.notistack-MuiContent-success': { backgroundColor: '#178a4c' },
  '&.notistack-MuiContent-error': { backgroundColor: '#d24a3b' },
  '&.notistack-MuiContent-warning': { backgroundColor: '#df8a30' },
  '&.notistack-MuiContent-info': { backgroundColor: '#243b82' },
  '&.notistack-MuiContent-default': { backgroundColor: '#243b82' },
}));

function AppContent() {
  const token = useAuthStore((state) => state.token);
  const clearToken = useAuthStore((state) => state.clearToken);
  const location = useLocation();

  const isPublicRoute = PUBLIC_ROUTES.some(route => location.pathname.startsWith(route));

  if (token) {
    try {
      const decodedToken = JSON.parse(atob(token.split('.')[1]));
      const currentTime = Math.floor(Date.now() / 1000);

      if (decodedToken.exp < currentTime) {
        clearToken();
      }
    } catch (e) {
      clearToken();
    }
  }

  if (isPublicRoute) {
    return (
      <Routes>
        <Route path="/camp/create" element={<CampCreatePage />} />
        <Route path="/invite/:token" element={<OrgInvitePage />} />
        <Route path="/login" element={<Login />} />
      </Routes>
    );
  }

  if (token) {
    return (
      <>
        <AppLayout />
        <Preloader/>
        <ModalConfirmation/>
      </>
    );
  }

  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="*" element={<Navigate to="/login" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <SnackbarProvider
        maxSnack={3}
        anchorOrigin={{vertical: 'top', horizontal: 'right'}}
        Components={{ success: AppSnackContent, error: AppSnackContent, warning: AppSnackContent, info: AppSnackContent, default: AppSnackContent }}
      >
        <ThemeProvider theme={baseTheme}>
          <CssBaseline/>
          <AppContent />
        </ThemeProvider>
      </SnackbarProvider>
    </QueryClientProvider>
  );
}

export default App;
